import React from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import { List, Map, fromJS } from 'immutable'
import { DOMfindParent, stopEvent } from '@aytm/lib/common/func'
import TextField from '@aytm/lib/rfd-ui/components/basic/text-field'
import LoaderWave from '@aytm/lib/rfd-ui/components/basic/loader-wave'
import Select from '@aytm/lib/rfd-ui/components/basic/select'
import railsPostFetch from '@aytm/lib/common/rails-post-fetch'

import '@aytm/lib/rfd-ui/styles/advanced/autocomplete.scss'
import '@aytm/lib/rfd-ui/styles/basic/select.scss'

const DEFAULT_LIST_MAXHEIGHT = 200

export default class AutocompleteInput extends Select {
  static propTypes = {
    placeholder: PropTypes.string,
    updateURL: PropTypes.string,
    width: PropTypes.number,
    debounce: PropTypes.number,
    listMaxHeight: PropTypes.number,
    highlight: PropTypes.bool,
    clearOnSelect: PropTypes.bool,
    onSearch: PropTypes.func,
    onChange: PropTypes.func,
    onSelect: PropTypes.func
  }

  constructor(props) {
    super(props)

    this.onFocus = this.onFocus.bind(this)
    this.onBlur = this.onBlur.bind(this)
    this.formatOptionsList = this.formatOptionsList.bind(this)
    this.selectOption = this.selectOption.bind(this)
    this.onListScroll = this.onListScroll.bind(this)
    this.clickOptionList = this.clickOptionList.bind(this)
    this.onChange = this.onChange.bind(this)
    this.fetchOptions = this.fetchOptions.bind(this)
    this.getOptions = this.getOptions.bind(this)

    this.state = {
      value: '',
      opened: false,
      options: List(),
      loading: false
    }

    this.updating = false
    this.debounceTimeout = null
    this.debounce = props.debounce >= 0 ? props.debounce : 1000
    this.options = null
    this.page = 0
    this.more = false
  }

  onFocus() {
    this.setState({ opened: true })
  }

  onBlur() {
    this.setState({ opened: false })
  }

  formatOptionsList(options) {
    const regex = new RegExp(`(${this.state.value.replace(/[$()*+.?[\\\]^{|}]/g, `\\$&`)})`, `gi`)
    return options.map(o => {
      const text =
        Map.isMap(o) && o.get('text')
          ? this.props.highlight
            ? o.get('text').replace(regex, `<span class='highlight'>$1</span>`)
            : o.get('text')
          : o
      const category = Map.isMap(o) && o.get('category') ? `<span class='category'>${o.get('category')}: </span>` : ``
      const span = `${category}<span class='text'>${text}</span>`

      return Map({
        id: Map.isMap(o) && o.get('id') ? o.get('id') : o,
        category: Map.isMap(o) && o.get('category') ? o.get('category') : '',
        value: Map.isMap(o) && o.get('text') ? o.get('text') : o,
        span: span
      })
    })
  }

  selectOption(option) {
    if (Map.isMap(option)) {
      option && 'function' == typeof this.props.onSelect && this.props.onSelect(option)
      option &&
        this.setState({
          value: this.props.clearOnSelect ? `` : option,
          opened: false,
          loading: false,
          options: List()
        })
    }
  }

  onListScroll() {
    if (
      this.options &&
      this.options.scrollHeight - this.options.scrollTop - 5 <= (this.props.listMaxHeight || DEFAULT_LIST_MAXHEIGHT) &&
      this.more &&
      !this.updating
    ) {
      this.updating = true
      this.setState({ loading: true })
      this.getOptions(this.state.value, this.page)
    }
  }

  applyOptions(data, page) {
    const list = this.formatOptionsList(fromJS(data.options))
    const opts = page ? List([...this.state.options, ...list]) : list

    this.setState({
      options: opts,
      loading: data.more
    })

    const overflow = (opts.length + 1) * 29 >= (this.props.listMaxHeight || DEFAULT_LIST_MAXHEIGHT)
    this.more && !overflow && this.getOptions(this.state.value, this.page)
  }

  clickOptionList(event) {
    stopEvent(event)

    const optionElement = event.target
      ? DOMfindParent(event.target, i => i.className && i.className.split(/\s+/).includes('rfd-ui-select-option'), 32)
      : null
    const optionIndex = optionElement ? optionElement.dataset.index : null

    if (null != optionIndex) {
      if (-1 == optionIndex) return false

      const optionIndexInt = parseInt(optionIndex, 10)
      if (!isNaN(optionIndexInt)) {
        const option = this.state.options.get(optionIndexInt)
        this.selectOption(option)
      }
    }

    return false
  }

  onChange(value) {
    if (value.length) {
      this.setState({
        loading: true,
        options: List()
      })

      if (this.debounce) {
        if (this.debounceTimeout) clearTimeout(this.debounceTimeout)
        this.debounceTimeout = setTimeout(() => {
          if (!this.mounted) return
          this.debounceTimeout = null
          if ('function' == typeof this.props.onChange) this.props.onChange(value)
          this.getOptions(value, 0)
        }, this.debounce)
      } else {
        'function' == typeof this.props.onChange && this.props.onChange(value)
        this.getOptions(value, 0)
      }

      if (this.options && this.options.scrollTop) this.options.scrollTop = 0
      this.setState({
        opened: true,
        value: value
      })
    } else
      this.setState({
        opened: false,
        value: value,
        loading: false,
        options: List()
      })
  }

  fetchOptions(value, page = 0) {
    if (this.props.updateURL) {
      const data = {
        text: value,
        page: page
      }
      return railsPostFetch(this.props.updateURL, {
        body: JSON.stringify(data)
      }).then(response => response.json())
    } else {
      if (this.props.onSearch) {
        const searchPromise = this.props.onSearch(value, page)
        if ('function' == typeof searchPromise.then) {
          return searchPromise
        }
      }
      return new Promise((_resolve, reject) => {
        reject('No source to update')
      })
    }
  }

  getOptions(text, page = 0) {
    this.fetchOptions(text, page)
      .then(data => {
        if (!this.mounted) return
        this.more = data.more
        this.page = page
        if (data.more) this.page += 1
        this.updating = false

        this.applyOptions(data, page)
      })
      .catch(_error => {
        //console.log(e)
        this.setState({ loading: false })
      })
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (nextProps.debounce) this.debounce = nextProps.debounce
  }

  filteredOptions() {
    return this.state.options
  }

  componentWillUnmount() {
    super.componentWillUnmount()
    if (this.debounceTimeout) clearTimeout(this.debounceTimeout)
  }

  render() {
    const { placeholder, width, className, listMaxHeight } = this.props
    const { opened, value, options, loading, keyboardIndex } = this.state

    return (
      <div className={classNames('rfd-ui-select rfd-ui-autocomplete', className)} style={{ width: width }}>
        <TextField
          width={width ? width : 200}
          value={`${Map.isMap(value) ? value.get('value') : value}`}
          placeholder={placeholder}
          readOnly={false}
          onChange={this.onChange}
          onFocus={this.onFocus}
          onBlur={this.onBlur}
          onKeyDown={this.onKeyDown}
        />
        {opened &&
          ((options && options.size > 0) || loading) &&
          (() => (
            <div className="rfd-ui-select-container">
              <ul
                className="rfd-ui-select-options"
                style={{
                  width: width ? width - 2 : 198,
                  maxHeight: listMaxHeight || DEFAULT_LIST_MAXHEIGHT
                }}
                onScroll={this.onListScroll}
                onMouseDown={this.clickOptionList}
                ref={ul => {
                  this.options = ul
                }}
              >
                {(() => {
                  const optionsMap = options.map((o, oi) => (
                    <li
                      className={classNames('rfd-ui-select-option', {
                        'rfd-ui-select-option-first': 0 == oi,
                        'rfd-ui-select-option-current': keyboardIndex == oi
                      })}
                      data-index={oi}
                      key={oi}
                    >
                      <div className="rfd-ui-select-option-text" dangerouslySetInnerHTML={{ __html: o.get('span') }} />
                    </li>
                  ))
                  const loadingSign = (
                    <li className="rfd-ui-select-option rfd-ui-loading-sign" data-index={-1} key={-1}>
                      <span className="rfd-ui-select-option-text rfd-ui-autocomplete-loader">
                        <LoaderWave bars={5} height={10} />
                      </span>
                    </li>
                  )
                  return loading ? optionsMap.set(optionsMap.size, loadingSign).toArray() : optionsMap.toArray()
                })()}
              </ul>
            </div>
          ))()}
      </div>
    )
  }
}
